import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Icon } from './Icon';

export interface InfoDotProps {
  /** Accessible label for the trigger — also the tooltip content when `children` is omitted. */
  text: string;
  /** Rich tooltip content (e.g. a diagram). Falls back to rendering `text` when omitted. */
  children?: ReactNode;
  /** Override the tooltip box's default `max-w-48` — needed when `children` is wider than text. */
  contentClassName?: string;
}

/**
 * Small info icon with a hover/focus/tap tooltip. Matches the design's chromeless inline trigger
 * (muted circle-info, no button background). The trigger keeps an `aria-label` for screen readers
 * and an invisible expanded hit-area (`after:-inset-2`) so it stays touch-tappable despite the
 * small glyph. `text` is always required for the label even when `children` supplies rich content
 * — a screen reader gets the label; the visual-only content (e.g. a diagram) is decorative.
 *
 * Controlled `open` state: Base UI's `TooltipTrigger` only wires up hover (`mouseOnly`) and
 * keyboard focus, so there's no tap-to-open path on touch devices. `onClick` always opens (never
 * toggles closed) — Base UI's own `closeOnClick` behavior already handles closing the tooltip when
 * its trigger is pressed again, so toggling here would race it. Tap the icon to open; tap
 * elsewhere or press Escape to close.
 */
export function InfoDot({ text, children, contentClassName }: InfoDotProps) {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger
          aria-label={text}
          onClick={() => setOpen(true)}
          className="relative inline-flex cursor-help text-muted-foreground after:absolute after:-inset-2 after:content-['']"
        >
          <Icon name="info" size="sm" />
        </TooltipTrigger>
        <TooltipContent className={cn('max-w-48', contentClassName)}>{children ?? text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
